import { message } from 'antd';
import { FC, type PropsWithChildren, createContext, useState } from 'react';
import { RouteObject } from 'react-router-dom';

import { Schemas } from '@api-client/generated/types';
import { isSessionActive, logout } from '@auth';
import { STORAGE_COMPANY_KEY } from '@constants';
// eslint-disable-next-line no-restricted-imports
import { getRoutes } from '@pages/getPaths';

type User = Schemas.User;
type AdminCompanyResponseDto = Schemas.AdminCompanyResponseDto;

type AccountContextProps = {
  isLogged: boolean;
  account: User | null;
  /**
   * @deprecated use useCurrentCompany instead
   */
  company: AdminCompanyResponseDto | null;
  /**
   * @deprecated use useCurrentCompanyId instead
   */
  companyId: string | null;
  role?: User['adminRole'];
  setAccount: (account: User) => void;
  /**
   * @deprecated don't set company, it has no use anymore
   */
  setCompany: (company: AdminCompanyResponseDto) => void;
  /** Ends the realm session, not just the local copy of it. */
  signOut: () => void;
  routerPaths: RouteObject[];
};

export const AccountContext = createContext<AccountContextProps>(
  {} as AccountContextProps
);

export const AccountProvider: FC<PropsWithChildren> = ({ children }) => {
  const [accountDetails, setAccountDetails] = useState<User | null>(null);
  const [selectedCompany, setSelectedCompany] =
    useState<AdminCompanyResponseDto>(() =>
      JSON.parse(localStorage.getItem(STORAGE_COMPANY_KEY)!)
    );

  const setAccount = (account: User) => setAccountDetails(account);

  const setCompany = (company: AdminCompanyResponseDto) => {
    setSelectedCompany(company);

    if (company) {
      localStorage.setItem(STORAGE_COMPANY_KEY, JSON.stringify(company));
    }
  };

  const signOut = () => {
    setAccountDetails(null);
    setSelectedCompany({} as AdminCompanyResponseDto);

    // Clearing local state alone would leave the realm session alive, so the next check-sso would
    // log the user straight back in. Say so if that request fails rather than dropping it: the realm
    // session is then still open, and a silent no-op reads as "logout is broken".
    logout().catch(() =>
      message.error('Could not log out. Please try again.')
    );
  };

  const routes = getRoutes(accountDetails?.adminRole) as RouteObject[];

  return (
    <AccountContext.Provider
      value={{
        // Continuity across reloads comes from the realm session cookie plus silent check-SSO, not
        // from a copy of the token in web storage.
        isLogged: isSessionActive(),
        account: accountDetails,
        company: selectedCompany,
        companyId: selectedCompany?.id || null,
        role: accountDetails?.adminRole,
        routerPaths: routes,
        setAccount,
        setCompany,
        signOut,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};
